import type { CatalogSeed, CatalogSeedRow } from '../pricing/seedBuilder';

/**
 * The µ$-valued cost kinds — the ONLY kinds this feature may author.
 *
 * The µ$/basis-point split is load-bearing (Global Constraints: "µ$ for `money`
 * kinds, basis points for `pct` kinds"), and it used to exist only as a
 * test-local constant in `importInertness.test.ts`. That is how a live defect
 * got in: with no production counterpart, `planWrites` scaled EVERY matched
 * component cell by 10⁶ regardless of kind, and `CatalogPage` rendered every
 * cell through µ$→dollars regardless of kind. `CMP-DISC-HW` (kind
 * `discountPct`, value `3000` = 30%) therefore showed as an ordinary editable
 * `$0.00` cell — `microsToCentsHalfEven(3000)` is 0 cents — and staging that
 * pre-filled default planned `value: '0'`, wiping the hardware discount, with a
 * preview that reported nothing moving (net prices do not depend on
 * `discountBp`; only list price and `SkuSheetEntry.discounts` do).
 *
 * So there is now exactly ONE definition, imported by the production write path
 * (`planWrites`), the production read path (`CatalogPage`) and the inertness
 * gate that first wrote it down.
 *
 * `integrationPct`/`discountPct` hold BASIS POINTS, not µ$ — restating '800' as
 * a dollar amount plans a garbage row. `unpriced` carries the inert placeholder
 * '0' and asserts the ABSENCE of a cost, so there is nothing to restate. Note
 * `planWrites`' `kindAt` never RETURNS 'unpriced' — a terminated SKU falls back
 * to the last kind that actually carried a cost — so re-pricing a terminated
 * component still works; this set only refuses the pct kinds in practice.
 */
export const COST_KINDS: ReadonlySet<string> = new Set<CatalogSeedRow['kind']>([
  'component', 'labor', 'chassis', 'baseRemainder',
]);

/** `COST_KINDS.has`, tolerant of the untyped `kind` a wire row carries — the
 *  JS callers (`CatalogPage`) have no compile-time union to lean on. */
export function isCostKind(kind: string | null | undefined): boolean {
  return typeof kind === 'string' && COST_KINDS.has(kind);
}

/**
 * The one shape every write path produces. Nothing downstream of the reader
 * knows whether a human typed this or a spreadsheet produced it — which is what
 * lets the manual editor and CSV import share a single set of rules for
 * immutability, sparseness and impact.
 */
export type CellTarget =
  | { type: 'component'; componentSku: string }
  | { type: 'product'; productSku: string };

export interface CostCell {
  ref: string;                   // as the source named it, pre-normalization
  target: CellTarget | null;     // set by matchDraft; null = unmatched
  quarter: string;               // '2026-Q3'
  /** A canonical DECIMAL string — '1234.56'. Separators and currency symbols
   *  stripped, sign normalized, NOT yet scaled.
   *
   *  The scale depends on the row type: a component's value is µ$, a product's
   *  is cents — four orders of magnitude apart — and which applies is unknown
   *  until matchDraft has classified the ref against the catalog. So the reader
   *  normalizes text and stops; planWrites scales, by target.type, with string
   *  arithmetic. Scaling in the reader would mean guessing the type from the SKU
   *  string, which is exactly the VCH-FRU-* trap. */
  amount: string;
}

export interface CostDraft {
  vendor: string;
  cells: CostCell[];
  sourceLabel: string;           // 'Q4-2026 costs.xlsx' or 'manual edit'
}

export type UnmatchedReason = 'unknown' | 'near-miss' | 'uncomposed' | 'ambiguous';

export interface UnmatchedCell {
  cell: CostCell;
  reason: UnmatchedReason;
  suggestion?: string;
}

export interface MatchedDraft {
  vendor: string;
  matched: CostCell[];           // every one has a non-null target
  unmatched: UnmatchedCell[];
}

/** A seed row that may still know its GraphQL id. `CatalogSeedRow` has none
 *  (`seedBuilder.ts:73-90`) and `seedFromRows` explicitly drops it
 *  (`rowsToSnapshot.ts:16-20`), even though the wire row carries it
 *  (`__fixtures__/catalogRows.ts:4-7`). Without this, `previousId` can never be
 *  populated and every row this feature writes has `supersedes: null` — the
 *  supersession chain would be silently worthless. `id` is optional, so every
 *  plain `CatalogSeed` and every fixture in this plan stays assignable.
 *
 *  Task 9 consequence: the hook must build its seed PRESERVING `id`, not via
 *  `seedFromRows`. */
export type AuthoredRow = CatalogSeedRow & { id?: string };
export interface AuthoredSeed {
  rows: AuthoredRow[];
  compositions: CatalogSeed['compositions'];
}

export interface PlannedRow {
  componentSku: string;          // for a product row this is CMP-BASE-<productSku>
  effectiveFrom: string;
  /** The CATALOG kind, taken from existing rows. NOT `string`: `CatalogSeedRow.kind`
   *  is a closed union (`seedBuilder.ts:85-86`), and `previewImpact` splices this
   *  straight into a `CatalogSeed` it hands to `buildSheet` — a widened type fails
   *  `npm run typecheck` there. Worse if the union is loosened instead: a bogus
   *  kind on a labor or chassis component drops it out of `upliftExemptSkus`
   *  (`seedBuilder.ts:253`) and it silently gets marked up by the integration rate. */
  kind: CatalogSeedRow['kind'];
  value: string;                 // integer µ$ string
  previousValue: string | null;  // null when nothing resolved before
  previousId: string | null;     // supersedes
  previousVersion: number;
  reassertion: boolean;
  /** Set only for a product row: the net price the PM asked for, in cents, and
   *  the SKU it belongs to. The review shows the PM this, not the solved
   *  remainder — the remainder is an implementation detail of how their price
   *  is stored, and no one can sanity-check it. */
  solvedFor?: { productSku: string; targetCents: number };
}

export interface NoOp {
  ref: string;
  quarter: string;
  amount: string;
}

/** A cell that matched but cannot be written. Distinct from unmatched: the ref
 *  resolved fine, the arithmetic refused.
 *
 *  `'malformed-amount'` (Task 5 review finding #3) is distinct from the other
 *  two: it fires before any solving happens, on `cell.amount` text that
 *  `scaleDecimal` cannot turn into an integer string — more precision than
 *  the target scale, or characters outside `-?\d+(\.\d+)?` (a stray comma,
 *  an Excel float artifact). `costDraft.ts`'s own doc comment on `amount`
 *  assigns that normalization to the reader, but `planWrites` is also the
 *  manual editor's write path (Task 9), so the guarantee cannot be assumed
 *  here — one malformed cell must become a `Blocked` entry, never abort the
 *  whole plan and lose every other cell's work.
 *
 *  `'duplicate-tuple'` (out-of-plan fix, filed off a Task 7 review finding)
 *  fires when two cells in ONE draft — a component cell authoring
 *  `CMP-BASE-X` directly, and a product cell pricing `X`, whose solved
 *  remainder ALSO targets `CMP-BASE-X` — resolve, only after `planWrites`'
 *  own pass 2 has mapped the product to its base SKU, to the SAME
 *  `(componentSku, effectiveFrom)` tuple. `matchDraft`'s Decision 3 cannot
 *  catch this: the two cells have different match targets going in.
 *  Written nowhere: `commitRows.ts`'s `buildCreateInputs` would otherwise
 *  emit two creates for one tuple, one of them carrying a false
 *  `supersedes: null`, and `supersedes` is immutable once written — this is
 *  the one failure mode in this module that cannot self-heal on a later
 *  commit.
 *
 *  `'not-a-cost-kind'` (whole-branch review finding C1) fires when a matched
 *  cell resolves to a componentSku whose catalog `kind` is NOT in
 *  `COST_KINDS` — today that means `integrationPct` or `discountPct`, whose
 *  `value` is BASIS POINTS. Every amount reaching `planWrites` is a decimal
 *  DOLLAR string, scaled by 10⁶ with no kind check, so writing one to a pct
 *  row stores a number four orders of magnitude wrong: staging `CMP-DISC-HW`
 *  at the `$0.00` the grid rendered for `3000` bp planned `value: '0'` and
 *  silently zeroed the 30% hardware discount, with a preview that reported
 *  nothing moving. This is the shared choke point — the manual editor and the
 *  CSV import both come through here — so the guard lives here rather than in
 *  either page. */
export interface Blocked {
  cell: CostCell;
  reason: 'infeasible' | 'negative-remainder' | 'malformed-amount' | 'duplicate-tuple'
        | 'not-a-cost-kind';
  detail: string;
}
